import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../utils/api';

const STORAGE_KEY = 'lj_customer_identity';

// Identidad de sesión del cliente en el sitio público. Es OPCIONAL y por
// teléfono (no hay login con contraseña). Sirve para que el cliente vea sus
// beneficios (precio de empleado, cupones) ANTES de armar el pedido. La
// aplicación real del beneficio sigue siendo autoritativa en el backend al
// cobrar; esto es solo informativo.
const CustomerIdentityContext = createContext(null);

export const useCustomerIdentity = () => {
  const ctx = useContext(CustomerIdentityContext);
  if (!ctx) throw new Error('useCustomerIdentity debe usarse dentro de CustomerIdentityProvider');
  return ctx;
};

const mapIdentity = (phone, data) => ({
  phone,
  name: data.name || '',
  customerType: data.customer_type || null,
  discountPct: data.discount_pct || 0,
  benefitType: data.benefit_type || null,
  benefitDailyLimit: data.benefit_daily_limit ?? null,
  benefitUsedToday: data.benefit_used_today || 0,
  coupons: data.coupons || [],
});

export const CustomerIdentityProvider = ({ children }) => {
  const [identity, setIdentity] = useState(null);
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [error, setError] = useState('');
  const [bootDone, setBootDone] = useState(false); // localStorage ya revisado (para el gate de entrada)

  const fetchIdentity = useCallback(async (phone) => {
    const res = await fetch(`${API_URL}/customers/verify-phone?phone=${encodeURIComponent(phone)}`);
    if (!res.ok) throw new Error('No se pudo verificar');
    const data = await res.json();
    if (data.status !== 'success' || data.exists !== 1) return null;
    return mapIdentity(phone, data);
  }, []);

  // Cargar identidad guardada y re-validar al montar (beneficios/uso cambian).
  useEffect(() => {
    let active = true;
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      saved = null;
    }
    if (saved && saved.phone) {
      setIdentity(saved); // optimista mientras revalida
      fetchIdentity(saved.phone)
        .then((fresh) => {
          if (!active) return;
          if (fresh) {
            setIdentity(fresh);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
          } else {
            setIdentity(null);
            localStorage.removeItem(STORAGE_KEY);
          }
        })
        .catch(() => {});
    }
    setBootDone(true);
    return () => { active = false; };
  }, [fetchIdentity]);

  const identify = useCallback(async (phone) => {
    setIsIdentifying(true);
    setError('');
    try {
      const fresh = await fetchIdentity(phone);
      if (!fresh) {
        setError('No encontramos ese teléfono. Lo registramos al hacer tu pedido.');
        return false;
      }
      setIdentity(fresh);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
      return true;
    } catch (e) {
      setError('Error al verificar. Intenta de nuevo.');
      return false;
    } finally {
      setIsIdentifying(false);
    }
  }, [fetchIdentity]);

  const clearIdentity = useCallback(() => {
    setIdentity(null);
    setError('');
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const hasBenefit = !!identity && (identity.benefitType === 'descuento' || identity.benefitType === 'cortesia');
  const hasCoupons = !!(identity && identity.coupons && identity.coupons.length > 0);
  const benefitRemaining = !identity || identity.benefitDailyLimit == null
    ? (hasBenefit ? Infinity : 0)
    : Math.max(0, Number(identity.benefitDailyLimit) - (identity.benefitUsedToday || 0));

  return (
    <CustomerIdentityContext.Provider
      value={{ identity, identify, clearIdentity, isIdentifying, error, hasBenefit, hasCoupons, benefitRemaining, bootDone }}
    >
      {children}
    </CustomerIdentityContext.Provider>
  );
};
